import { supabase } from '@/lib/supabase';
import type { FurniturePiece } from '@/types';
import { furnitureStoragePath, fileExt } from '@/lib/furnitureCatalog';

const STORAGE_BUCKET = 'images';

export interface FurniturePieceWithUrl extends FurniturePiece {
  url: string;
}

export async function listFurniturePieces(): Promise<FurniturePieceWithUrl[]> {
  const { data, error } = await supabase
    .from('furniture_pieces')
    .select('*')
    .order('category')
    .order('name');

  if (error) throw error;

  return ((data as FurniturePiece[]) || []).map((row) => ({
    ...row,
    url: supabase.storage.from(STORAGE_BUCKET).getPublicUrl(row.storage_path).data.publicUrl,
  }));
}

export async function createFurniturePiece(input: {
  file: File;
  category: string;
  name: string;
}): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const id = crypto.randomUUID();
  const path = furnitureStoragePath(id, fileExt(input.file));

  const upload = await supabase.storage.from(STORAGE_BUCKET).upload(path, input.file, {
    contentType: input.file.type,
    upsert: false,
  });
  if (upload.error) throw new Error(`Upload fehlgeschlagen: ${upload.error.message}`);

  const { error } = await supabase.from('furniture_pieces').insert({
    id,
    category: input.category,
    name: input.name,
    storage_path: path,
    created_by: user.id,
  });
  if (error) throw error;
}

export async function updateFurniturePiece(
  id: string,
  input: { name: string; category: string; file?: File | null },
): Promise<void> {
  const patch: { name: string; category: string; storage_path?: string } = {
    name: input.name,
    category: input.category,
  };

  let oldPath: string | null = null;
  if (input.file) {
    // Aktuellen Pfad merken, um das alte Bild nach dem Update zu entfernen.
    const { data: cur, error: curErr } = await supabase
      .from('furniture_pieces')
      .select('storage_path')
      .eq('id', id)
      .single();
    if (curErr) throw curErr;
    oldPath = cur?.storage_path ?? null;

    const newId = crypto.randomUUID();
    const path = furnitureStoragePath(newId, fileExt(input.file));
    const upload = await supabase.storage.from(STORAGE_BUCKET).upload(path, input.file, {
      contentType: input.file.type,
      upsert: false,
    });
    if (upload.error) throw new Error(`Upload fehlgeschlagen: ${upload.error.message}`);
    patch.storage_path = path;
  }

  const { error } = await supabase.from('furniture_pieces').update(patch).eq('id', id);
  if (error) throw error;

  // Altes Bild aufräumen (non-fatal), erst nachdem das Update durch ist.
  if (oldPath && patch.storage_path) {
    await supabase.storage.from(STORAGE_BUCKET).remove([oldPath]);
  }
}

export async function deleteFurniturePiece(id: string): Promise<void> {
  const { data, error: fetchError } = await supabase
    .from('furniture_pieces')
    .select('storage_path')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  const { error } = await supabase.from('furniture_pieces').delete().eq('id', id);
  if (error) throw error;

  // Storage-Cleanup ist non-fatal (Zeile darf nicht „hängen" bleiben, wenn das
  // Bild schon weg ist).
  await supabase.storage.from(STORAGE_BUCKET).remove([data.storage_path]);
}

export async function toggleFurniturePieceActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('furniture_pieces')
    .update({ is_active: isActive })
    .eq('id', id);
  if (error) throw error;
}

export interface CatalogImportPreview {
  name: string;
  category: string;
  images: string[];
}

/** Zieht die spezifische Server-Fehlermeldung aus einem supabase FunctionsHttpError
 *  (dessen `context` ein Response-Objekt ist), sonst den deutschen Fallback. */
async function extractFunctionError(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: unknown }).context;
  if (ctx instanceof Response) {
    try {
      const body = (await ctx.json()) as { error?: unknown };
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
    } catch {
      // Body nicht lesbar/kein JSON → Fallback.
    }
  }
  return fallback;
}

/** Lädt eine Shop-Produktseite serverseitig und liefert Bildkandidaten + Auto-Name/Kategorie. */
export async function importPiecePreview(url: string): Promise<CatalogImportPreview> {
  const { data, error } = await supabase.functions.invoke('catalog-import', {
    body: { action: 'preview', url },
  });
  if (error) {
    console.error('[Catalog] preview failed', error);
    throw new Error(await extractFunctionError(error, 'Seite konnte nicht geladen werden'));
  }
  return data as CatalogImportPreview;
}

/** Übernimmt das gewählte Bild in die Möbel-Bibliothek (Download + Upload + INSERT serverseitig). */
export async function importPieceCommit(args: {
  imageUrl: string;
  name: string;
  category: string;
}): Promise<void> {
  const { error } = await supabase.functions.invoke('catalog-import', {
    body: { action: 'commit', ...args },
  });
  if (error) {
    console.error('[Catalog] commit failed', error);
    throw new Error(await extractFunctionError(error, 'Import fehlgeschlagen'));
  }
}
